import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {
    Bin as DeleteIcon,
    Group as UsersIcon,
    Home as InternalUserIcon,
    Plus as AddUserIcon,
    SendMail as SendOTPSecret
} from "iconoir-react";
import {ComponentType, useEffect, useRef, useState} from 'react'
import {Dropdown, Navbar} from "react-bootstrap";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Nav from "react-bootstrap/Nav";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {SortDirection, UserApi, UserLocality, type UserValue} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {SortIcon} from "../common/SortIcon";
import {Truncate} from "../common/Truncate";
import {useHasAllPermissions} from "../hook/UseHasAllPermissions";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {SubLevelSettingsPopup} from "../settings/SubLevelSettingsPopup";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {SendOTPSecretByEmailPopup} from "./SendOTPSecretByEmail";

/*
 * REST api for accessing the back-end
 */
const userApi = new UserApi(ApiConfig.config());

interface UsersProps {
    AdditionalMenuItems?: ComponentType<{
        className?: string;
    }>
}

/**
 * Users component.
 *
 * Provides functionality to manage users, including searching, pagination, sorting, adding and deleting users.
 * Renders a table of users with options to select and perform actions on users.
 *
 * @returns {ReactElement} The Users component.
 */
export const Users = ({AdditionalMenuItems} : UsersProps) => {
    /**
     * The path segments have the following structure
     *
     * path :== /domain/<category>/<domain> | /user/<category>/<email>
     *
     * <category> :== <category-name> | <category-name>/<category>
     * <category-name> :== <letter> | <letter><category-name>
     */
    const location = useLocation()
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The loaded users
     */
    const [users, setUsers] = useState<UserValue[]>([]);
    /**
     * The total number of users
     */
    const [userCount, setUserCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of users to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The sort direction of the users
     */
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Asc);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * The selected email addresses
     */
    const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);
    /**
     * If true, the "add user" popup dialog will be shown
     */
    const [showAddUser, setShowAddUser] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" popup dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "send otp secret by email" modal dialog will be shown
     */
    const [showSendOTPSecret, setShowSendOTPSecret] = useState<boolean>(false);
    /**
     * If true, the users will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * The error message if a validation error occurred (for example email address is invalid)
     */
    const [validationErrorMessage, setValidationErrorMessage] = useState<string>();
    /**
     * Reference to the email address field from the add email address modal. This is used to get the email address.
     */
    const emailControlRef = useRef<HTMLInputElement>(null);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /*
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        userApi.userControllerSearchUsersCount(`%${activeSearchValue??""}%`)
            .then(response => {
                setUserCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting user count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        activeSearchValue,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setErrorMessage(undefined);
        setSelectedUsers([]);
        setShowAddUser(false);
        setShowDeleteConfirm(false);
        setForceReload(false);
        setValidationErrorMessage(undefined);

        userApi.userControllerSearchUsers(`%${activeSearchValue??""}%`, sortDirection, (currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setUsers(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting users", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
    }, [navigate,
        activeSearchValue,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        sortDirection,
        t
    ]);

    function getLinkTarget(email: string): string {
        return "/users/settings/general/" + encodeURIComponent(email);
    }

    const onChangeSelect = (checked : boolean, email : string) =>
    {
        setSelectedUsers(checked ?
            [...selectedUsers, email] :
            selectedUsers.filter(e => e !== email)
        );
    }

    const addUser = () =>
    {
        const emailAddress = emailControlRef.current?.value;

        if (emailAddress === undefined || emailAddress.trim().length === 0)
        {
            setValidationErrorMessage("Email address is required")

            return;
        }

        userApi.userControllerAddUser(emailAddress)
            .then(() => {
                setForceReload(true);
                // navigate to settings for the user
                setShowAddUser(false);
                navigate(getLinkTarget(emailAddress));
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error adding user", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setValidationErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    const showAddUserPopup = () =>
    {
        // reset error message
        setValidationErrorMessage(undefined);
        setShowAddUser(true)
    }

    const deleteSelected = () =>
    {
        userApi.userControllerDeleteUsers(selectedUsers)
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting users", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                setShowDeleteConfirm(false);
                setSelectedUsers([]);
            })
    }

    const invertSelection = () => {
        // users that are in use should never be selected
        setSelectedUsers([...users].filter(u => !selectedUsers.includes(u.email))
            .filter(u => !u.inUse).map(u => u.email));
    }

    return (
        <>
            <Modal show={showAddUser} onHide={() => setShowAddUser(false)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t("users.add-user")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputGroup className="mb-3">
                        <InputGroup.Text>@</InputGroup.Text>
                        <Form.Control
                            type="email"
                            placeholder={t("users.email-address")}
                            ref={emailControlRef}
                            isInvalid={validationErrorMessage !== undefined}
                            autoFocus/>
                    </InputGroup>
                    <RenderAlert message={validationErrorMessage}/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => addUser()}>
                        {t("users.add")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Popup dialog which is shown when showDeleteConfirm is true */}
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("users.body-text")}
                           confirmButtonTitle={t("users.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {/* Popup dialog which is shown when setShowSendOTPSecret is true */}
            {showSendOTPSecret && <SendOTPSecretByEmailPopup onClose={() => setShowSendOTPSecret(false)} />}

            {/^\/users\/.+/i.test(location.pathname) && <SubLevelSettingsPopup />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("users.title")} icon={<UsersIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <InputGroup>
                                <Form.Control
                                              aria-label={t("users.search-value")}
                                              type="search"
                                              placeholder={t("users.search")}
                                              value={searchValue}
                                              onChange={e => setSearchValue(e.target.value)}
                                />
                                <Button
                                        variant="primary"
                                        onClick={() => setActiveSearchValue(searchValue)}
                                        disabled={activeSearchValue === searchValue}>
                                    {t("users.search")}
                                </Button>
                            </InputGroup>
                        </div>

                        <div className="ms-4"/>

                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => showAddUserPopup()} disabled={!hasAllPermissions(["user:add-user"])}>
                                <AddUserIcon className="text-success"/> {t("users.add-user")}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => setShowSendOTPSecret(true)} disabled={!hasAllPermissions(["portal:portal-send-otp-secret-by-email"])}>
                                <SendOTPSecret className="text-success"/> {t("users.send-otp-secret")}
                            </Dropdown.Item>

                            {AdditionalMenuItems !== undefined && <AdditionalMenuItems/>}
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <thead className="sticky-top">
                        <tr>
                            {selectedUsers.length === 0 ?
                                <>
                                    <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                    <th>{t("users.email")} <SortIcon sortDirection={sortDirection} setSortDirection={setSortDirection}/></th>
                                    <th>{t("users.created")} </th>
                                    <th>{t("users.comment")} </th>
                                </>
                                :
                                <th colSpan={4}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedUsers([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedUsers.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link disabled={!hasAllPermissions(["user:delete-users"])} onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("users.delete-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {
                            users.map((user) => (
                                <tr key={user.email} className="text-nowrap cursor-pointer">
                                    <td className="td-w-min">
                                        <div className="d-flex align-items-center">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedUsers.includes(user.email)}
                                                onChange={(e) => onChangeSelect(e.target.checked, user.email)}
                                                disabled={user.inUse}
                                                inline
                                            />

                                            {user.locality === UserLocality.Internal &&
                                                <OverlayTrigger
                                                    placement={right}
                                                    overlay={
                                                        <Tooltip>
                                                            {t("users.internal-user")}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <InternalUserIcon className="text-success table-icon-width table-icon-height ms-1"/>
                                                </OverlayTrigger>
                                            }
                                        </div>
                                    </td>
                                    <td className={"user-select-all"} onClick={() => navigate(getLinkTarget(user.email))}><Truncate value={user.email} maxLength={60}/></td>
                                    <td className={"user-select-all"} onClick={() => navigate(getLinkTarget(user.email))}>{DateUtils.timestampToDateStringShort(user.created)}</td>
                                    <td className={"user-select-all"} onClick={() => navigate(getLinkTarget(user.email))}><Truncate value={user.comment} maxLength={240}/></td>
                                </tr>
                            ))
                        }
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {userCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={userCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
